import { useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { logScoutEvent, getBuildHash } from '@appic/lib/utils/scoutLog'
import { installSwUpdateReload } from '../../lib/chunkReload'

/**
 * Service worker auto-update registration.
 *
 * With registerType: 'autoUpdate', new service workers activate immediately.
 * This component just registers the SW and polls for updates every 30 minutes
 * so long-running iPad PWA sessions pick up deploys without a manual refresh.
 *
 * Diagnostic events emitted:
 * - sw_registered: SW successfully registered
 * - sw_update_found: new SW version detected (waiting to activate)
 * - sw_register_error: SW registration failed
 */
export function SWUpdatePrompt() {
  // Reload once when a newly-deployed service worker takes control, so an
  // open PWA session runs the fresh assets instead of the pre-deploy chunk
  // graph (NEVER-DARK §A). Loop-guarded + ignores the cold-start controllerchange.
  useEffect(() => {
    const teardown = installSwUpdateReload()
    return teardown
  }, [])

  useRegisterSW({
    onRegistered(registration) {
      if (registration) {
        logScoutEvent('sw_registered', {
          build_hash: getBuildHash(),
          scope: registration.scope,
          active: !!registration.active,
        })

        // Poll for SW updates every 30 minutes (iPad PWA stays open all day)
        setInterval(() => registration.update(), 30 * 60 * 1000)

        // Detect when a new SW is waiting to activate
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && registration.active) {
                logScoutEvent('sw_update_found', {
                  build_hash: getBuildHash(),
                  new_sw_state: newWorker.state,
                })
              }
              if (newWorker.state === 'activated') {
                logScoutEvent('sw_activated', {
                  build_hash: getBuildHash(),
                })
              }
            })
          }
        })
      }
    },
    onRegisterError(error) {
      console.warn('[SW] Registration error:', error)
      logScoutEvent('sw_register_error', {
        error: error instanceof Error ? error.message : String(error),
        build_hash: getBuildHash(),
      })
    },
  })

  // autoUpdate mode — no prompt needed, SW activates silently
  return null
}
